/**
 * demoFixture — one week of invented subcontractor work, with every case the
 * payroll run knows how to be wrong about.
 *
 * WHAT IT IS FOR. Mikko, 2026-09-13: "can we have test data to simulate?" HR
 * has to be able to walk the six steps — set the dates, clear Needs attention,
 * check the drafts, save, issue, pay — without a real subcontractor's money on
 * the screen and without writing a row to SharePoint. Reading the live lists
 * teaches the happy path only: the interesting cases are rare in any one week
 * and nobody can conjure one on demand.
 *
 * SO EVERY CASE IS IN HERE, ON PURPOSE, ONE OF EACH:
 *
 *   a plain set          ABAD, priced both bands
 *   a top only           BAUTISTA, design band and no trousers
 *   a pants only         BAUTISTA, the other half of the same order
 *   an assumed plan      CRUZ, no WO Log behind it, so the picker shows
 *   a rework             DIZON, two sets back with a reason, never paid
 *   an unknown spelling  "ESTRDA", which nobody has mapped yet
 *   a shared row         "CRUZ/DIZON", reported and paid to neither
 *   in-house work        "BY OPERATION", never paid
 *   an orphan            a pass with no slip and no sewer behind it
 *   no rate band         a ZZ99 that no band prices
 *   a pants gap          a PRIMO whose trousers have no band
 *   already paid         last week's payslip, so PAID shows a reference
 *
 * THE DATES MOVE WITH TODAY. A fixture pinned to September 2026 would fall out
 * of the run's window the week after it was written and show an empty screen,
 * which is the most confusing possible result for somebody being taught the
 * screen. Everything here is built relative to the day it is asked for.
 *
 * THE NAMES ARE INVENTED. Not one of them is a real subcontractor, the
 * references all start with D, and the whole module refuses to write anything.
 * A demo payslip must never be mistakable for a document somebody was paid on.
 *
 * Pure and I/O-free: it builds objects and hands them back.
 */
import type { IPayrollProfile } from "../payrollProfile";
import type { IPayout } from "../payout";
import type { IRateRow } from "../rateCard";
import type { IQcScan } from "../../mfg-workcenters/qcLedger";
import type { IReceivingScan } from "../../mfg-workcenters/receivingLedger";
import type { ISlip } from "../../../lib/sp/DispatchService";
import type { IProductionWip } from "../../../lib/sp/ProductionGridService";
import type { IWorkbookSheetRow } from "../../mfg-workcenters/workbookRecent";
import type { StoredWipPlans, TagPlan } from "../wipPlan";
import type { StoredWipPants } from "../wipPants";
import type { StoredKeyPayable } from "../payableQty";
import type { StoredLineEdits } from "../lineEdits";
import type { StoredKeyOverrides } from "../keyOverrides";
import { DEFAULT_ADDITIONS, NO_DEDUCTIONS } from "../payrollMath";
import { RATE_CARD } from "../rateCard";

/** ISO day `n` days before `iso`, in UTC so it never drifts a day. */
export function dayBefore(iso: string, n: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - n * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

const at = (iso: string, hhmm = "09:00"): string => `${iso}T${hhmm}:00Z`;

export interface IDemoData {
  profiles: IPayrollProfile[];
  payouts: IPayout[];
  rateCard: IRateRow[] | null;
  qcScans: IQcScan[];
  slips: ISlip[];
  wips: IProductionWip[];
  receivingScans: IReceivingScan[];
  workbookRows: IWorkbookSheetRow[];
  workbookRepairRows: IWorkbookSheetRow[];
  woLogPlans: Map<string, TagPlan>;
  wipPlans: StoredWipPlans;
  /** WIP -> the pants band a person chose (wipPants.ts). */
  wipPants: StoredWipPants;
  /** Passes held off or added by hand — see keyOverrides.ts. */
  keyOverrides: StoredKeyOverrides;
  /** Passes paid at fewer sets than QC passed — see payableQty.ts. */
  keyPayable: StoredKeyPayable;
  /** Hand-typed rate / sets on priced bands — see lineEdits.ts. */
  lineEdits: StoredLineEdits;
  pantsMap: Record<string, string>;
  aliases: Record<string, string>;
}

const profile = (
  n: number, ledgerKey: string, given: string, bir: "BIR" | "NON-BIR",
  mode: "BANK TRANSFER" | "GCASH" | "CASH", schedule: string,
): IPayrollProfile => ({
  spId: 900 + n,
  ledgerKey,
  fullName: `${ledgerKey}, ${given}`,
  contractorId: `IG9000${n}`,
  bir,
  companyName: bir === "BIR" ? `${ledgerKey} TAILORING (DEMO)` : undefined,
  tin: bir === "BIR" ? `000-000-000-00${n}` : undefined,
  bankName: `${given.split(" ")[0]} ${ledgerKey}`,
  bankAccount: `00${n}9876543${n}`,
  modeOfPayment: mode,
  location: "DASMARIÑAS, CAVITE",
  section: "DEMO",
  schedule,
  cellphone: `0917000000${n}`,
  position: "SEWER SUBCONTRACTOR",
  active: true,
});

const wip = (
  wipId: string, design: string, sku: string, qty: number, serialId: string,
): IProductionWip => ({
  spId: Number(wipId.slice(-5)),
  wipId, sku, qty, design, serialId,
  storeChannel: "SHP IG",
  currentStage: "BQC5",
}) as IProductionWip;

const slip = (n: number, dsNumber: string, sewer: string, wipIds: string[], day: string): ISlip => ({
  spId: 800 + n, dsNumber, sewer, sewerCode: "", wipIds,
  totalSets: wipIds.length, totalPieces: wipIds.length,
  slipStatus: "Issued", generatedBy: "DEMO", generatedAt: at(day, "08:00"),
  signatoryDispatchedBy: "DEMO", signatoryPreparedBy: "DEMO", printCount: 1,
});

/** A QC sheet row, the shape workbookPassEvents reads. */
const sheetRow = (
  rowNumber: number, wipId: string, sku: string, sewer: string, date: string,
  qty: number, serial: string, stage: "QCSRC" | "QCREP" = "QCSRC", remarks?: string,
): IWorkbookSheetRow => ({
  stage, wipId, date, qty, rowNumber, who: "DEMO QC",
  extra: [
    { label: "SKU", value: sku },
    { label: "Store", value: "SHP IG" },
    { label: "Serial ID", value: serial },
    { label: "Sewer", value: sewer },
    ...(remarks ? [{ label: "Remarks", value: remarks }] : []),
  ],
});

/**
 * The whole demo dataset for a given "today".
 *
 * `todayIso` is the run's own date, so the work lands inside the default
 * window (period start is six days back, "pay QC'd through" is today).
 */
export function buildDemoData(todayIso: string): IDemoData {
  const d1 = dayBefore(todayIso, 1);
  const d2 = dayBefore(todayIso, 2);
  const d3 = dayBefore(todayIso, 3);
  const lastWeekEnd = dayBefore(todayIso, 7);

  const profiles: IPayrollProfile[] = [
    profile(1, "ABAD", "ROSA DEMO", "NON-BIR", "BANK TRANSFER", "MONDAY"),
    profile(2, "BAUTISTA", "MERCY DEMO", "BIR", "BANK TRANSFER", "MONDAY"),
    profile(3, "CRUZ", "ELENA DEMO", "NON-BIR", "BANK TRANSFER", "MONDAY"),
    profile(4, "DIZON", "MARIA DEMO", "NON-BIR", "GCASH", "WEDNESDAY"),
    profile(5, "ESTRADA", "LUZ DEMO", "BIR", "BANK TRANSFER", "FRIDAY"),
  ];

  // ABAD's bundles come through the app's own QC station, so they exercise the
  // slip-and-grid join. Everybody else comes off the QC sheet, which is where
  // most of a real week still comes from.
  const wips: IProductionWip[] = [
    wip("D9000101", "SS 028", "SS028-MDBLU-M", 6, "D-45001-1"),
    wip("D9000102", "SS 032", "SS032-BLK-L", 4, "D-45002-1"),
    // The bundle that carries ABAD over the twenty-set transpo minimum.
    wip("D9000103", "SS 028", "SS028-ARGRN-XL", 13, "D-45001-3"),
    wip("D9000201", "SS 032", "SS032-MDBLU-S", 2, "D-46536-2"),
    wip("D9000202", "SS 032", "SS032-MDBLU-M", 2, "D-46536-2"),
    wip("D9000301", "SS 028", "SS028-ARGRN-L", 5, "D-45003-1"),
    wip("D9000401", "SS 033", "SS033-WHT-M", 4, "D-45004-1"),
    wip("D9000501", "SS 075", "SS075-VLT-LLC-M", 3, "D-45005-1"),
    wip("D9000601", "ZZ 99", "ZZ99-NOSUCH-M", 2, "D-45006-1"),
    wip("D9000701", "SS 028", "SS028-WHT-S", 3, "D-45007-1"),
    wip("D9000801", "SS 028", "SS028-MDBLU-M", 1, "D-45001-9"),
    wip("D9000901", "SS 028", "SS028-MDBLU-L", 4, "D-45009-1"),
  ];

  const slips: ISlip[] = [
    slip(1, "DS D-4001", "ABAD", ["D9000101", "D9000102", "D9000103"], d3),
    slip(2, "DS D-4002", "BAUTISTA", ["D9000201", "D9000202"], d3),
    slip(3, "DS D-4003", "CRUZ", ["D9000301"], d3),
    slip(4, "DS D-4004", "DIZON", ["D9000401"], d3),
    slip(5, "DS D-4005", "ESTRADA", ["D9000501", "D9000601"], d3),
    // D9000701 deliberately has NO slip: that is the orphan.
  ];

  // BQC4 took everything back the day before QC saw it.
  const receivingScans: IReceivingScan[] = wips
    .filter(w => w.wipId !== "D9000701")
    .map((w, i) => ({
      spId: 700 + i, wipId: w.wipId, dsNumber: "DS D-400" + ((i % 5) + 1),
      qty: w.qty, receivedBy: "DEMO BQC4", receivedAt: at(d2, "14:00"),
    }));

  // ABAD, through the app's QC station: one clean pass and one split pass.
  const qcScans: IQcScan[] = [
    { spId: 601, wipId: "D9000101", qty: 6, outcome: "pass", qcBy: "DEMO QC", qcAt: at(d1, "10:00") },
    { spId: 602, wipId: "D9000102", qty: 3, outcome: "pass", qcBy: "DEMO QC", qcAt: at(d1, "10:30") },
    { spId: 603, wipId: "D9000102", qty: 1, outcome: "repair", qcBy: "DEMO QC", qcAt: at(d1, "10:31") },
    // 6 + 3 + 13 = 22 sets delivered, so ABAD's payslip carries the ₱200
    // transpo allowance and everybody else's does not. The demo has to hold
    // BOTH sides of a threshold or it cannot teach one (Mikko, 2026-09-15).
    { spId: 604, wipId: "D9000103", qty: 13, outcome: "pass", qcBy: "DEMO QC", qcAt: at(d1, "11:00") },
  ];

  const workbookRows: IWorkbookSheetRow[] = [
    // The split order: one customer, a small top and medium pants.
    sheetRow(101, "D9000201", "SS032-MDBLU-S", "BAUTISTA", d1, 2, "D-46536-2"),
    sheetRow(102, "D9000202", "SS032-MDBLU-M", "BAUTISTA", d1, 2, "D-46536-2"),
    // CRUZ: no WO Log behind this WIP, so its plan is assumed and the run says so.
    sheetRow(103, "D9000301", "SS028-ARGRN-L", "CRUZ", d1, 5, "D-45003-1"),
    // DIZON: four passed, two went back for a rework (below).
    sheetRow(104, "D9000401", "SS033-WHT-M", "DIZON", d1, 4, "D-45004-1"),
    // ESTRADA, misspelt on the sheet the way the real one is misspelt.
    sheetRow(105, "D9000501", "SS075-VLT-LLC-M", "ESTRDA", d1, 3, "D-45005-1"),
    // A design no band prices.
    sheetRow(106, "D9000601", "ZZ99-NOSUCH-M", "ESTRADA", d1, 2, "D-45006-1"),
    // Two names on one row: somebody has to split it by hand.
    sheetRow(107, "D9000401", "SS033-WHT-M", "CRUZ/DIZON", d2, 1, "D-45004-2"),
    // The floor's own work, never paid here. On a WIP of its own: a sheet row
    // for a WIP the app also has a pass scan for is dropped as superseded
    // (qcEvents.mergeEvents), which would quietly cost the demo this case.
    sheetRow(108, "D9000801", "SS028-MDBLU-M", "BY OPERATION", d2, 1, "D-45001-9"),
    // No sewer and no slip: nobody can be paid for it, which is the case that
    // costs somebody a week's pay silently.
    sheetRow(109, "D9000701", "SS028-WHT-S", "", d1, 3, "D-45007-1"),
    // LAST week's work, on last week's payslip. Outside this run's window, so
    // it changes nothing on Drafts; it is here so the QC WIPs tab can show a
    // row whose PAID column names a real payslip.
    sheetRow(110, "D9000901", "SS028-MDBLU-L", "ABAD", lastWeekEnd, 4, "D-45009-1"),
  ];

  const workbookRepairRows: IWorkbookSheetRow[] = [
    sheetRow(201, "D9000401", "SS033-WHT-M", "DIZON", d1, 2, "D-45004-1", "QCREP", "COLLAR REPAIR"),
  ];

  // The Cutting J.O. Log's own instruction, for the WIPs it covers. CRUZ's
  // bundle is deliberately absent so the run has something to ask about.
  const woLogPlans = new Map<string, TagPlan>([
    ["D9000101", "BOTH"],
    ["D9000102", "BOTH"],
    ["D9000103", "BOTH"],
    ["D9000201", "TOP"],
    ["D9000202", "PANTS"],
    ["D9000401", "BOTH"],
    ["D9000501", "BOTH"],
    ["D9000601", "BOTH"],
    ["D9000701", "BOTH"],
  ]);

  // Last week's payslip, already paid, so the QC WIPs tab has something under
  // PAID and the run has a cursor to start after.
  const paid: IPayout = {
    spId: 950,
    payoutRef: `D0001-${lastWeekEnd}`,
    sewer: "ABAD",
    contractorId: "IG90001",
    contractorName: "ABAD, ROSA DEMO",
    bir: "NON-BIR",
    section: "DEMO",
    periodStart: dayBefore(lastWeekEnd, 6),
    periodEnd: lastWeekEnd,
    dateIssued: lastWeekEnd,
    modeOfPayment: "BANK TRANSFER",
    bankName: "ROSA ABAD",
    bankAccount: "0019876543",
    schedPayout: "MONDAY",
    // THE RATE KEY MUST BE ONE THE CARD ACTUALLY CARRIES. The payslip screen
    // renders expandToCard, which walks the card and looks each line up by
    // rateKey, so an invented key vanishes from the table while payoutTotals
    // still counts it — a payslip reading "No lines" under a total of 480.
    // elite-design-3 is the ₱120 band SS028 really sits on.
    lines: [{
      rateKey: "elite-design-3", code: "E3", group: "ELITE DESIGN",
      item: "SS 028, 030, 031, 031A, 031B, 033, 043, 045, 048, 049, 052",
      rate: 120, qty: 4, source: "bqc5",
      wipIds: ["D9000901"], designs: ["028"],
      // A key from a REAL pass row (below), so the QC WIPs tab's PAID column
      // has something to name and its Paid filter is not empty.
      scanKeys: [`wb:${lastWeekEnd}:D9000901:4:D-45009-1`],
    }],
    additions: { ...DEFAULT_ADDITIONS },
    deductions: { ...NO_DEDUCTIONS },
    status: "Paid",
    createdBy: "DEMO", createdAt: at(lastWeekEnd, "08:00"),
    issuedBy: "DEMO", issuedAt: at(lastWeekEnd, "09:00"),
    paidBy: "DEMO", paidAt: at(lastWeekEnd, "10:00"),
  };

  return {
    profiles,
    payouts: [paid],
    // A provisioned card, so Needs attention holds only this week's work. A
    // null here would fire the "shipped rates" warning, which is about how the
    // site is set up rather than about the week, and the demo exists to teach
    // the week.
    rateCard: [...RATE_CARD],
    qcScans, slips, wips, receivingScans,
    workbookRows, workbookRepairRows,
    woLogPlans,
    wipPlans: {},
    wipPants: {},
    keyOverrides: {},
    keyPayable: {},
    lineEdits: {},
    pantsMap: {},
    // ESTRDA is left OUT on purpose: mapping it is step two of the walkthrough.
    aliases: {},
  };
}
